/**
 * Pure product-name utilities shared across selectors and slices.
 * No store dependency — these are stateless string transforms.
 */

const SUPERMARKET_PREFIX = /^(ah|jumbo|plus|lidl|aldi|dirk|dekamarkt|ekoplaza|bio)\s+/gi
const BIO_KEYWORDS = /\b(biologisch|biologische|bio|eko|organic)\b/gi

/**
 * Normalize a product name into a canonical key for cross-supermarket comparison.
 *
 * @param {string} name - Raw product name
 * @returns {string} Normalised key (lowercase, no brand prefix, no bio labels)
 */
export function normalizeProductKey(name) {
  return name
    .toLowerCase()
    .replace(SUPERMARKET_PREFIX, '')
    .replace(BIO_KEYWORDS, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Strip supermarket brand prefixes and bio labels for display.
 *
 * @param {string} name - Raw product name
 * @returns {string} Cleaned label for UI display
 */
export function cleanProductLabel(name) {
  return name
    .replace(/^(AH|Jumbo|Plus|Lidl|Aldi|Dirk|DekaMarkt|Ekoplaza)\s+/i, '')
    .replace(/\b(Biologisch|Biologische|Bio)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Generate Dutch stem variants for fuzzy matching.
 *
 * @param {string} word - Input word (≥3 chars for stemming)
 * @returns {Array<string>} Original word plus stem variants
 */
export function dutchStems(word) {
  if (word.length < 3) return [word]
  const stems = [word]
  const suffixes = ['en', 's', 'nen', 'ten', 'sche', 'se', 'e']
  for (const suffix of suffixes) {
    if (word.endsWith(suffix) && word.length > suffix.length + 2) {
      stems.push(word.slice(0, -suffix.length))
    }
  }
  return [...new Set(stems)]
}
